import React from "react";
import { useTheme, Box, Stack, Paper, Typography, Chip, Tooltip, IconButton, Menu, MenuItem } from "@mui/material";
import { AddCircle, Edit, DeleteForever, MoreVert } from "@mui/icons-material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";

import { ISODateTimeString } from "components/formatting/ISODateTime";
import { InfegyUser } from "system/InfegyUser";

interface TeamComponentProps {
  team: TeamType;
  users: InfegyUser[];
  
  onEditTeam: (teamID: string) => void;
  onDeleteTeam: (teamID: string) => void;
  onAddMember: (teamID: string) => void;
  onRemoveMember: (teamID: string, member: TeamMember) => void;
}

export interface TeamType {
  id: string; // Team ID, eg; "t_a1b2c3"
  user_id: string; // Team owner, userID, eg; "u_a1b2c3"
  name: string;
  description: string;
  members?: TeamMember[]; // List of team members
  _owned_by_you: boolean;
  account_id: string; 
  created_at: string;
  deleted_at: string | null;
  updated_at: string;
}

export interface TeamMember {
  id: string; // Team Member ID, eg; "tm_a1b2c3
  user_id: string; // User ID, eg; "u_a1b2c3"
  team_id: string; // Team ID, eg; "t_a1b2c3"
  created_at: string;
  deleted_at: string | null;
}

export default function Team(props: TeamComponentProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const {team, users, onEditTeam, onDeleteTeam, onAddMember, onRemoveMember} = props;

  const created = ISODateTimeString(i18n, team.created_at);
  const description = team.description;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <Paper sx={{display: "flex", flexDirection: "column", py: 1, px: 2, mb: 2}}>

        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%"}}>
          <Box>
            <Typography variant="h3">{team.name}</Typography>
          </Box>
          <Box>
            <Stack direction="row" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              {team._owned_by_you && (
                <Box>
                  {isMobile ? (
                    <>
                      <IconButton
                        aria-label="more"
                        aria-controls="team-actions-menu"
                        aria-haspopup="true"
                        onClick={handleMenuOpen}
                      >
                        <MoreVert />
                      </IconButton>
                      <Menu
                        id="team-actions-menu"
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem onClick={() => { handleMenuClose(); onAddMember(team.id); }}>
                          <AddCircle sx={{ mr: 1 }} color="success" /> {i18n.t("teams.actions.add-team-member")}
                        </MenuItem>
                        <MenuItem onClick={() => { handleMenuClose(); onEditTeam(team.id); }}>
                          <Edit sx={{ mr: 1 }} /> {i18n.t("teams.actions.edit-team")}
                        </MenuItem>
                        <MenuItem onClick={() => { handleMenuClose(); onDeleteTeam(team.id); }}>
                          <DeleteForever sx={{ mr: 1 }} color="error" /> {i18n.t("teams.actions.delete-team")}
                        </MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <>
                      <Tooltip followCursor title={i18n.t("teams.actions.add-team-member")}>
                        <IconButton color="success" aria-label="Add Team Member" onClick={() => onAddMember(team.id)}>
                          <AddCircle/>
                        </IconButton>
                      </Tooltip>
                      <Tooltip followCursor title={i18n.t("teams.actions.edit-team")}>
                        <IconButton color="default" aria-label="Edit Team" onClick={() => onEditTeam(team.id)}>
                          <Edit/>
                        </IconButton>
                      </Tooltip>
                      <Tooltip followCursor title={i18n.t("teams.actions.delete-team")}>
                        <IconButton color="error" aria-label="Delete Team" onClick={() => onDeleteTeam(team.id)} >
                          <DeleteForever/>
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Details */}
        <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap", mt: 1 }}>
          <Stack sx={{ pr: 1, width: isMobile ? "50%" : "300px" }}>
            <Typography variant="body1">
              {i18n.t("created")}: <strong>{created}</strong>
            </Typography>
            {description && 
              <Typography variant="body1">
                {i18n.t("description")}: <strong>{description}</strong>
              </Typography>
            }
          </Stack>

          {/* Members */}
          <Stack sx={{ width: isMobile ? "50%" : "calc(100% - 300px)" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {team.members?.map((member:TeamMember) => (
                <Chip 
                  key={member.id} 
                  label={users.find((user) => user.id === member.user_id)?.name || "Unknown User"}
                  {...(team._owned_by_you && {
                    onDelete: () => onRemoveMember(team.id, member),
                  })}
                  sx={{ "& .MuiChip-deleteIcon": { color: theme.palette.text.disabled } }}
                />
              ))}
            </Box>
          </Stack>
        </Box>
      </Paper>
    </Box>
  )
}
